import { ReactElement, cloneElement, useState } from 'react';

import { BRANDED_LOGOS, getGenericIcon } from './integrationIcons';

type IntegrationIconProps = {
  /** The uploaded icon URL from the backend (`nodetype_icon`), if any. Wins when set. */
  iconUrl?: string | null;
  /** The stable integration identifier (`nodetype_slug`), used for the fallback logo/icon. */
  slug?: string | null;
  /** Alt text for image logos. */
  name?: string | null;
  /** Class applied to the `<img>` (uploaded icon or branded logo). */
  imageClassName?: string;
  /** Class applied to the fallback AntD icon. Defaults to `imageClassName`. */
  iconClassName?: string;
};

/**
 * Renders an integration's icon with a graceful fallback chain:
 *   1. the uploaded backend icon (`iconUrl` / `nodetype_icon`);
 *   2. a bundled branded logo matched on `slug`;
 *   3. a generic AntD icon for the slug's category;
 *   4. the default `ApiOutlined` plug.
 *
 * A broken uploaded URL (e.g. missing media file) degrades to the slug-based
 * fallback instead of showing a broken image.
 */
const IntegrationIcon = ({
  iconUrl,
  slug,
  name,
  imageClassName,
  iconClassName,
}: IntegrationIconProps) => {
  const [uploadedFailed, setUploadedFailed] = useState(false);

  const brandedLogo = slug ? BRANDED_LOGOS[slug] : undefined;
  const alt = name || slug || '';

  if (iconUrl && !uploadedFailed) {
    return (
      <img
        src={iconUrl}
        alt={alt}
        className={imageClassName}
        onError={() => setUploadedFailed(true)}
      />
    );
  }

  if (brandedLogo) {
    return <img src={brandedLogo} alt={alt} className={imageClassName} />;
  }

  const genericIcon = getGenericIcon(slug) as ReactElement;
  const fallbackClassName = iconClassName ?? imageClassName;

  return fallbackClassName
    ? cloneElement(genericIcon, { className: fallbackClassName })
    : genericIcon;
};

export default IntegrationIcon;
